import React, { useMemo } from "react";
import { Table, Typography, Badge, Divider } from "antd";
import { Line, Pie } from "react-chartjs-2";
import educationImg from "../../../assets/images/education3.png";
import futurProjectionImg from "../../../assets/images/up_down.png";
import currencyImg from "../../../assets/images/currency3.png";
import breakdownImg from "../../../assets/images/performance.png";
import "./EducationProjection.css";

const { Title: AntTitle, Text, Paragraph } = Typography;

const EducationProjection = ({ payload, taxInfo, rateOfReturnData }) => {
  // Format currency helper
  const formatCurrency = (amount) => {
    const num = Number(amount) || 0;
    return "$" + num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Extract data from payload
  const {
    // Student & Timeline Fields
    "Person Attending School": studentName = "",
    "Name of Institution": institutionName = "",
    "Level of Education": educationLevel = "",
    "Student's Current Age": currentAge = 0,
    "Age Student Will Begin This School": enrollmentAge = 0,
    // Education Details
    "Estimated Annual Cost of Attendance (Today's Dollars)": annualCost = 0,
    "Tuition and Fees": tuitionFees = 0,
    "Housing (Room)": housing = 0,
    "Meal Plan (Board)": mealPlan = 0,
    "Books, Supplies, & Equipment": books = 0,
    "Personal & Transportation Expenses": personal = 0,

    // Funding Sources
    "Amount Received": friendsFamilyReceived = 0,
    "Friends and Family – Multiple Payment Amount": friendsFamilyMultiple = 0,
    "Friends and Family – One-Time Payment Amount": friendsFamilyOneTime = 0,
    "Net Proceeds": netProceeds = 0,
    "Amount Available to Assign": amountAvailable = 0,
    "Monthly Contribution Amount": monthlyContribution = 0,
    "Amount of Payment": studentLoanPayment = 0,
    "Student Loan Amount": studentLoanAmount = 0,
    "Scholarship and Grant Amount": scholarshipAmount = 0,
    "Initial Loan Amount": personalLoanAmount = 0,
    "Annual Work-Study Award Amount": workStudyAmount = 0,

    // Additional fields for projection
    "Years Until Attendance": yearsUntil = 0,
    "Number of Years to Fund": yearsToFund = 4,
  } = payload;

  // Calculate inflation rate from taxInfoAllData (General Inflation)
  const inflationInput = "Education";
  const matchedInflation =
    taxInfo && taxInfo.find((item) => item.name === inflationInput);
  const inflationRate = matchedInflation
    ? (parseFloat(matchedInflation.value) || 0) / 100
    : 0.05; // Default to 5% as per document

  // Calculate rate of return from rateOfReturnData or payload
  const returnRate = (payload && payload["Assumed Annual Investment Return (%)"]) || (rateOfReturnData && rateOfReturnData.staticPercentage);
  const rateOfReturn = returnRate || 0;

  // Logic: Enrollment Age – Current Age defines the "Goal Start Date"
  const yearsUntilAttendance = enrollmentAge > 0 && currentAge > 0
    ? Math.max(0, enrollmentAge - currentAge)
    : Number(yearsUntil);

  // Calculate totals
  const totalCostToday =
    Number(tuitionFees) +
    Number(housing) +
    Number(mealPlan) +
    Number(books) +
    Number(personal);

  // Calculate total projected cost with inflation across all years of funding
  // Future Cost Year 1 = Today’s Cost x (1+Inflation)^(Enrollment Age – Current Age)
  const totalProjectedCost = useMemo(() => {
    let total = 0;
    for (let i = 0; i < Number(yearsToFund); i++) {
      const yearsFromNow = yearsUntilAttendance + i;
      total += totalCostToday * Math.pow(1 + inflationRate, yearsFromNow);
    }
    return total;
  }, [totalCostToday, inflationRate, yearsUntilAttendance, yearsToFund]);

  const friendsFamilyTotal =
    Number(friendsFamilyReceived) +
    Number(friendsFamilyMultiple) +
    Number(friendsFamilyOneTime);
  const savingsTotal =
    Number(netProceeds) + Number(amountAvailable) + Number(monthlyContribution);
  const studentLoansTotal =
    Number(studentLoanPayment) + Number(studentLoanAmount);
  const scholarshipsTotal = Number(scholarshipAmount);
  const personalLoanTotal = Number(personalLoanAmount);
  const workStudyTotal = Number(workStudyAmount);

  const totalFunding =
    friendsFamilyTotal +
    savingsTotal +
    studentLoansTotal +
    scholarshipsTotal +
    personalLoanTotal +
    workStudyTotal;

  const projectedShortfall = totalProjectedCost - totalFunding;

  // Projected Costs Breakdown Table Data
  const costsColumns = [
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
    },
    {
      title: "Annual Cost (Today)",
      dataIndex: "annualCost",
      key: "annualCost",
      align: "right",
      render: (value) => formatCurrency(value),
    },
    {
      title: "Total Projected Cost (Future)",
      dataIndex: "totalCost",
      key: "totalCost",
      align: "right",
      render: (value) => formatCurrency(value),
    },
  ];

  const costsData = [
    {
      key: "1",
      category: "Tuition & Fees",
      annualCost: tuitionFees,
      totalCost: Array.from({ length: Number(yearsToFund) }).reduce((acc, _, i) =>
        acc + Number(tuitionFees) * Math.pow(1 + inflationRate, yearsUntilAttendance + i), 0),
    },
    {
      key: "2",
      category: "Room & Board",
      annualCost: Number(housing) + Number(mealPlan),
      totalCost: Array.from({ length: Number(yearsToFund) }).reduce((acc, _, i) =>
        acc + (Number(housing) + Number(mealPlan)) * Math.pow(1 + inflationRate, yearsUntilAttendance + i), 0),
    },
    {
      key: "3",
      category: "Books & Other",
      annualCost: Number(books) + Number(personal),
      totalCost: Array.from({ length: Number(yearsToFund) }).reduce((acc, _, i) =>
        acc + (Number(books) + Number(personal)) * Math.pow(1 + inflationRate, yearsUntilAttendance + i), 0),
    },
    {
      key: "total",
      category: <strong>Total Cost</strong>,
      annualCost: <strong>{totalCostToday}</strong>,
      totalCost: <strong>{totalProjectedCost}</strong>,
    },
  ];

  // Projected Funding Sources Breakdown Table Data
  const fundingColumns = [
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      align: "center",
    },
    {
      title: "Projected Value",
      dataIndex: "value",
      key: "value",
      align: "right",
      render: (value) => formatCurrency(value),
    },
  ];

  const fundingData = [
    {
      key: "1",
      source: "Friends and Family",
      type: "Gift",
      value: friendsFamilyTotal,
    },
    {
      key: "2",
      source: "Dedicated Savings",
      type: "Savings",
      value: savingsTotal,
    },
    {
      key: "3",
      source: "Student Loans",
      type: "Debt",
      value: studentLoansTotal,
    },
    {
      key: "4",
      source: "Scholarships",
      type: "Gift",
      value: scholarshipsTotal,
    },
    {
      key: "5",
      source: "Personal Loan / HELOC",
      type: "Debt",
      value: personalLoanTotal,
    },
    {
      key: "6",
      source: "Work Study",
      type: "Income",
      value: workStudyTotal,
    },
    {
      key: "total",
      source: <strong>Total</strong>,
      type: "",
      value: <strong>{totalFunding}</strong>,
    },
  ];

  // Generate year-by-year projection data for the line chart
  const generateProjectionData = () => {
    const years = [];
    const line1Data = []; // Total Cost of Attendance (Cumulative)
    const line2Data = []; // Dedicated Savings and Investments (Cumulative)

    const currentYear = new Date().getFullYear();
    const startYear = currentYear + yearsUntilAttendance;
    const endYear = startYear + Number(yearsToFund);

    let cumulativeCost = 0;
    let cumulativeSavings = 0;

    // Show projection from today until end of school
    for (let year = currentYear; year <= endYear; year++) {
      years.push(year.toString());
      const yearsFromNow = year - currentYear;

      // Cost only starts at startYear
      if (year >= startYear && year < endYear) {
        const annualProjectedCost = totalCostToday * Math.pow(1 + inflationRate, yearsFromNow);
        cumulativeCost += annualProjectedCost;
      } else if (year >= endYear) {
        // Keep cumulative cost flat after school ends
      }

      // Savings grow over time (simplified for visualization)
      // In a real scenario, we'd account for rate of return and contributions
      const annualSavings = totalFunding / (endYear - currentYear + 1);
      cumulativeSavings += annualSavings;

      line1Data.push(cumulativeCost / 1000); // Convert to thousands
      line2Data.push(cumulativeSavings / 1000); // Convert to thousands
    }

    return { years, line1Data, line2Data };
  };

  const projectionData = useMemo(() => generateProjectionData(), [
    totalCostToday,
    inflationRate,
    yearsUntilAttendance,
    yearsToFund,
    totalFunding
  ]);

  // Line Chart Configuration
  const lineChartData = {
    labels: projectionData.years,
    datasets: [
      {
        label: "Total Cost of Attendance",
        data: projectionData.line1Data,
        borderColor: "#2A3DA3",
        backgroundColor: "rgba(79, 172, 254, 0.1)",
        lineTension: 0.4,
        fill: true,
      },
      {
        label: "Dedicated Savings and Investments",
        data: projectionData.line2Data,
        borderColor: "#51cf66",
        backgroundColor: "rgba(81, 207, 102, 0.1)",
        lineTension: 0.4,
        fill: true,
      },
    ],
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    legend: {
      display: true,
      position: "bottom",
    },
    title: {
      display: true,
      text: "Cost vs. Savings Projection",
      fontSize: 16,
    },
    tooltips: {
      callbacks: {
        label: function (tooltipItem, data) {
          const label = data.datasets[tooltipItem.datasetIndex].label || "";
          return label + ": $" + tooltipItem.yLabel.toFixed(2) + "k";
        },
      },
    },
    scales: {
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
            callback: function (value) {
              return "$" + value;
            },
          },
          scaleLabel: {
            display: true,
            labelString: "Amount ($1000s)",
          },
        },
      ],
      xAxes: [
        {
          scaleLabel: {
            display: true,
            labelString: "Year",
          },
        },
      ],
    },
  };

  // Pie Chart Configuration
  const pieChartData = {
    labels: [
      "Savings",
      "Student Loans",
      "Friends/Family",
      "Scholarships",
      "Personal Loan",
      "Work Study",
    ],
    datasets: [
      {
        data: [
          savingsTotal,
          studentLoansTotal,
          friendsFamilyTotal,
          scholarshipsTotal,
          personalLoanTotal,
          workStudyTotal,
        ],
        backgroundColor: [
          "#2a3da3", // Savings - Bright Blue
          "#fa709a", // Student Loans - Pink
          "#fee140", // Friends/Family - Yellow
          "#09A84D", // Scholarships - Teal
          "#a8edea", // Personal Loan - Light Cyan
          "#ffd89b", // Work Study - Peach
        ],
        hoverBorderColor: "#E53E3E",
        hoverBorderWidth: 2,
      },
    ],
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    legend: {
      display: true,
      position: "right",
      labels: {
        padding: 15,
        fontSize: 11,
      },
    },
    title: {
      display: true,
      text: "Funding Mix",
      fontSize: 16,
    },
    tooltips: {
      callbacks: {
        label: function (tooltipItem, data) {
          const label = data.labels[tooltipItem.index] || "";
          const value = data.datasets[0].data[tooltipItem.index];
          const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
          const percentage = ((value / total) * 100).toFixed(1);
          return (
            label + ": " + formatCurrency(value) + " (" + percentage + "%)"
          );
        },
      },
    },
  };

  return (
    <div className="education-projection-container">
      {/* Header Section */}
      <div className="projection-header">
        <div className="header-content">
          <div className="header-icon">
            <span className="icon-graduation">
              <img
                src={educationImg}
                width={56}
                height={56}
                alt="Education Icon"
              />
            </span>
          </div>
          <div className="header-text">
            <AntTitle level={3} style={{ margin: 0 }}>
              Education Expense Projection {studentName ? `for ${studentName}` : ""}
            </AntTitle>
            <div style={{ display: "flex", gap: "16px", marginTop: "4px", flexWrap: "wrap" }}>
              {institutionName && (
                <Badge status="processing" text={`Institution: ${institutionName}`} />
              )}
              {educationLevel && (
                <Badge status="warning" text={`Level: ${educationLevel}`} />
              )}
              {enrollmentAge > 0 && (
                <Badge status="success" text={`Enrollment Age: ${enrollmentAge}`} />
              )}
            </div>
            <Paragraph
              style={{
                margin: "8px 0 0 0",
                fontSize: "14px",
              }}
            >
              Projections based on{" "}
              <strong>{(inflationRate * 100).toFixed(2)}% inflation rate</strong> and{" "}
              <strong>{rateOfReturn}% rate of return</strong>.
              Starting in <strong>{yearsUntilAttendance} years</strong> for <strong>{yearsToFund} years</strong>.
            </Paragraph>
          </div>
        </div>
      </div>

      {/* Summary Section */}
      <div className="projection-summary-card">
        <div className="summary-row">
          <div className="summary-col">
            <div className="summary-item cost-item">
              <div className="summary-icon-wrapper cost-icon">
                <span className="summary-icon">
                  <img
                    width={64}
                    height={64}
                    src={futurProjectionImg}
                    alt="Future Icon"
                  />
                </span>
              </div>
              <div className="summary-details">
                <Text className="summary-label">
                  Total Projected Future Cost
                </Text>
                <AntTitle level={3} className="summary-value cost">
                  {formatCurrency(totalProjectedCost)}
                </AntTitle>
                <Badge
                  count={`${yearsToFund} years`}
                  style={{ backgroundColor: "#ff6b6b" }}
                />
              </div>
            </div>
          </div>
          <div className="summary-col">
            <div className="summary-item funding-item">
              <div className="summary-icon-wrapper funding-icon">
                <span className="summary-icon">
                  <img
                    width={64}
                    height={64}
                    src={currencyImg}
                    alt="Currency Icon"
                  />
                </span>
              </div>
              <div className="summary-details">
                <Text className="summary-label">Total Projected Funding</Text>
                <AntTitle level={3} className="summary-value funding">
                  {formatCurrency(totalFunding)}
                </AntTitle>
                <Badge
                  count="Available"
                  style={{ backgroundColor: "#51cf66" }}
                />
              </div>
            </div>
          </div>
          <div className="summary-col">
            <div className="summary-item shortfall-item">
              <div
                className={`summary-icon-wrapper ${projectedShortfall > 0 ? "shortfall-icon" : "surplus-icon"
                  }`}
              >
                <span className="summary-icon">
                  {projectedShortfall > 0 ? "⚠️" : "✅"}
                </span>
              </div>
              <div className="summary-details">
                <Text className="summary-label">
                  {projectedShortfall > 0
                    ? "Projected Shortfall"
                    : "Projected Surplus"}
                </Text>
                <AntTitle
                  level={3}
                  className={`summary-value ${projectedShortfall > 0 ? "shortfall" : "surplus"
                    }`}
                >
                  {formatCurrency(Math.abs(projectedShortfall))}
                </AntTitle>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Badge
                    count={projectedShortfall > 0 ? "Action Needed" : "On Track"}
                    style={{
                      backgroundColor:
                        projectedShortfall > 0 ? "#ffa94d" : "#20c997",
                    }}
                  />
                  {projectedShortfall < 0 && (
                    <Text style={{ fontSize: '11px', color: '#20c997', fontStyle: 'italic' }}>
                      Surplus moved to Future Assets
                    </Text>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Net Proceeds Calculation Section */}
      {netProceeds > 0 && (
        <>
          <Divider
            orientation="left"
            style={{ fontSize: "18px", fontWeight: 600, color: "#495057" }}
          >
            <span className="section-icon">💰</span> Asset Liquidation Details
          </Divider>
          <div className="projection-card" style={{ marginBottom: 32 }}>
            <div className="card-header">
              <div className="card-title-wrapper">
                <span>Net Proceeds Calculation (Illiquid Assets)</span>
              </div>
              <Badge count="Verified Truth" style={{ backgroundColor: "#2a3da3" }} />
            </div>
            <Table
              pagination={false}
              columns={[
                { title: "Description", dataIndex: "desc", key: "desc" },
                { title: "Formula / Logic", dataIndex: "logic", key: "logic" },
                { title: "Amount", dataIndex: "amount", key: "amount", align: "right", render: (val) => formatCurrency(val) },
              ]}
              dataSource={[
                {
                  key: "1",
                  desc: "Gross Sale Price (Future Value)",
                  logic: "Market Value at Goal Date",
                  amount: payload["Gross Sale Price"] || (Number(netProceeds) * 1.5), // Fallback for demo
                },
                {
                  key: "2",
                  desc: "Estimated Selling Costs",
                  logic: "Commission & Fees (~7%)",
                  amount: -(payload["Estimated Selling Costs"] || (Number(netProceeds) * 0.1)),
                },
                {
                  key: "3",
                  desc: "Debt Payoff",
                  logic: "Mortgage / Liens",
                  amount: -(payload["Debt Payoff"] || 0),
                },
                {
                  key: "4",
                  desc: "Estimated Taxes",
                  logic: "Capital Gains Tax",
                  amount: -(payload["Estimated Tax"] || (Number(netProceeds) * 0.15)),
                },
                {
                  key: "total",
                  desc: <strong>Net Proceeds Available</strong>,
                  logic: <strong>Final Funding Amount</strong>,
                  amount: <strong>{netProceeds}</strong>,
                },
              ]}
            />
            <Paragraph style={{ marginTop: 16, fontSize: "12px", color: "#666", fontStyle: "italic" }}>
              * Excess proceeds from illiquid asset sales are automatically moved to your Future Asset table.
            </Paragraph>
          </div>
        </>
      )}

      <Divider
        orientation="left"
        style={{ fontSize: "18px", fontWeight: 600, color: "#495057" }}
      >
        <span className="section-icon">
          <img
            width={48}
            height={48}
            src={breakdownImg}
            alt="Financial Breakdown"
          />
        </span>{" "}
        Financial Breakdown
      </Divider>

      {/* Tables Section */}
      <div className="tables-row" style={{ marginBottom: 32 }}>
        <div className="table-col">
          <div className="projection-card costs-card">
            <div className="card-header">
              <div className="card-title-wrapper">
                <span>Projected Costs Breakdown</span>
              </div>
              <Badge
                count={`${yearsToFund} Years`}
                style={{ backgroundColor: "#667eea" }}
              />
            </div>
            <Table
              columns={costsColumns}
              dataSource={costsData}
              pagination={false}
            />
          </div>
        </div>
        <div className="table-col">
          <div className="projection-card funding-card">
            <div className="card-header">
              <div className="card-title-wrapper">
                <span>Projected Funding Sources</span>
              </div>
              <Badge count="6 Sources" style={{ backgroundColor: "#51cf66" }} />
            </div>
            <Table
              columns={fundingColumns}
              dataSource={fundingData}
              pagination={false}
            />
          </div>
        </div>
      </div>

      <Divider
        orientation="left"
        style={{ fontSize: "18px", fontWeight: 600, color: "#495057" }}
      >
        <span className="section-icon">
          <img
            width={48}
            height={48}
            src={breakdownImg}
            alt="Visual Analytics"
          />
        </span>{" "}
        Visual Analytics
      </Divider>

      {/* Charts Section */}
      <div className="charts-row">
        <div className="chart-col chart-col-line">
          <div className="projection-card chart-card line-chart-card">
            <div className="card-header">
              <div className="card-title-wrapper">
                <span>Savings Projection Timeline</span>
              </div>
            </div>
            <div className="chart-wrapper">
              <Line data={lineChartData} options={lineChartOptions} />
            </div>
          </div>
        </div>
        <div className="chart-col chart-col-pie">
          <div className="projection-card chart-card pie-chart-card">
            <div className="card-header">
              <div className="card-title-wrapper">
                <span>Funding Distribution</span>
              </div>
              <Badge
                count={`${formatCurrency(totalFunding)}`}
                style={{ backgroundColor: "#764ba2" }}
                overflowCount={999999999}
              />
            </div>
            <div className="chart-wrapper pie-chart-wrapper">
              <Pie data={pieChartData} options={pieChartOptions} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EducationProjection;
